import { useMutation } from '@tanstack/react-query';
import { Col, Form, Row } from 'antd';
import { DefaultOptionType } from 'antd/es/select';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { SaleTeamApi } from 'apis/SaleTeamApi';
import { SaleTeamDetailApi } from 'apis/SaleTeamDetailApi';
import { Button, ButtonFilter } from 'components';
import ButtonExport from 'components/Button/ButtonExport';
import ButtonImport from 'components/Button/ButtonImport';
import { UploadModal } from 'components/UploadModal_V2';
import { useTitle } from 'contexts/TitleProvider';
import { useUser } from 'contexts/UserProvider';
import useQueryParam from 'hooks/useQueryPrams';
import { ColumnsTypeCustom } from 'interfaces/Table/ColumnType';
import {
  GetTeamSales,
  IFilterTeamSale,
  IFilterTeamSaleCb,
  IGetAllFilterTeamSales,
  IParamsCreate,
  IParamsUpdate,
  Item,
  PageInfo,
  STATUS_NAME_ST,
  STATUS_TS,
  TEAM_TYPE,
  TteamSalesDetail,
} from 'interfaces/TeamSale/TeamSale';
import {
  IParamsAddEmployee,
  IParamsUpdateManyEmployee,
} from 'interfaces/TeamSaleDetail/TeamSaleDetail';
import { DEFAULT_PAGE_SIZE } from 'utils/constants/constant';
import { exportExcel_v2 } from 'utils/constants/exportExcel';
import { toastError, toastSuccess } from 'utils/constants/functions';
import { icons } from 'utils/constants/icons';
import { IMPORT_SALES_TEAM } from 'utils/constants/importId';
import { TEAM_TYPE_OPTIONS } from 'utils/constants/TeamSale/TeamSale';
import FilterSaleTeamPage from './FilterSaleTeamPage';
import TableSaleTeamPage from './TableSaleTeamPage';

const { PlusOutlined } = icons;

const SaleTeamPage = () => {
  const navigate = useNavigate();

  // const [form] = Form.useForm();
  const { user } = useUser();
  const { setTitle, setBreadcrumb } = useTitle();

  const childRef = useRef<any>(null),
    filterRef = useRef<any>(null),
    firR = useRef(true);

  const [isOpenFilter, setIsOpenFilter] = useState(false),
    [data, setData] = useState<GetTeamSales>({
      items: [],
      pageInfo: {} as PageInfo,
    } as GetTeamSales),
    [edit, setEdit] = useState(-1),
    [paramsFilter, setParamsFilter] = useState<IFilterTeamSale>(
      {} as IFilterTeamSale,
    ),
    [isAddRow, setIsAddRow] = useState(false),
    [isOpenImportModal, setIsOpenImportModal] = useState(false),
    [teamData, setTeamData] = useState<{ items: Item[]; totalItems: number }>({
      items: [] as Item[],
      totalItems: 0,
    });

  const defaultFilter: IGetAllFilterTeamSales = useMemo(() => {
    if (user?.isCN)
      return {
        statusId: STATUS_TS.active,
        plantId: user.plantCode,
        ...(user?.isQLD && { managerId: user.id }),
      };
    if (user?.isAdmin || user?.isHO) return { statusId: STATUS_TS.active };
    // default is sale permission
    return {
      statusId: STATUS_TS.active,
      userId: user?.id,
      plantId: user?.plantCode ?? '',
    };
  }, [user]);

  const queryParam = useQueryParam(),
    page = parseInt(queryParam.get('page') + '') || 1,
    pageSize = parseInt(queryParam.get('page_size') + '') || DEFAULT_PAGE_SIZE;

  const getTeamSalesMutation = useMutation({
      mutationFn: (
        params: IGetAllFilterTeamSales & {
          export?: { headerRow: ColumnsTypeCustom };
          afterMutation?(items: Pick<GetTeamSales, 'items'>): void;
        },
      ) => SaleTeamApi.getAllFilterTeamSales(params),
      onSuccess: (res, variables) => {
        if (res.data?.getTeamSalesList) {
          const { afterMutation } = variables;
          const items = res?.data?.getTeamSalesList?.items.map((item: any) => {
            try {
              return {
                ...item,
                createBy: item.tcreateUser?.fullName ?? '',
                updateBy: item?.tmodifyUser?.fullName ?? '',
                leader: item?.userLeader,
                employees: item?.teamUserName,
              };
            } catch (error) {
              console.log(error);
              return item;
            }
          });

          afterMutation && afterMutation(items);

          if (!res.data.getTeamSalesList) return;

          // form?.resetFields();

          if (typeof variables.export !== 'undefined') {
            const headers = variables.export.headerRow.map((column: any) =>
              column.dataIndex == 'manageName'
                ? {
                    ...column,
                    render: (value: any, record: any) =>
                      (value &&
                        `${record?.manageEmail} - ${record?.manageName}`) ||
                      '-',
                  }
                : column,
            );

            exportExcel_v2(headers, items, 'Kế hoạch - Đội bán hàng');
          } else if (isAddRow) {
            setData({
              ...res.data.getTeamSalesList,
              items: [{ statusId: STATUS_TS.active }, ...items],
            });
          } else setData({ ...res.data.getTeamSalesList, items: items });
        }
      },
      onError: (error) => {
        console.log(error);
      },
    }),
    { isPending: isGetTeamSaleCbb, mutate: getTeamSaleCbb } = useMutation({
      mutationFn: (params: IFilterTeamSaleCb) =>
        SaleTeamApi.getAllTeamSaleCb(params),
      onSuccess: (res) => {
        if (res?.data?.getTeamSales)
          setTeamData({
            items: res?.data?.getTeamSales.items ?? [],
            totalItems: res?.data?.getTeamSales.pageInfo.totalItems ?? 0,
          });
      },
      onError: (error) => {
        console.log(error);
      },
    }),
    { isPending: isDeleteSaleTeamPending, mutate: deleteSaleTeam } =
      useMutation({
        mutationFn: (saleTeam: string) => SaleTeamApi.deleteSaleTeam(saleTeam),
        onSuccess: (res) => {
          console.log(res);
        },
        onError: (error) => {
          console.log(error.message);
        },
      }),
    {
      isPending: isCreateEmployeeMutationPending,
      mutateAsync: createManyEmployee,
    } = useMutation({
      mutationFn: ({
        params,
      }: {
        params: IParamsAddEmployee[];
        record?: any;
        successMessage?: string;
        nextMutate?: () => void;
      }) => SaleTeamDetailApi.createManyTeamSaleDetail(params),
      onSuccess: (res, variables) => {
        if (res.data.createManyTeamSalesDetail) {
          const { nextMutate } = variables;

          if (typeof nextMutate !== 'undefined') {
            nextMutate();
            return;
          }

          toastSuccess(variables?.successMessage ?? 'Tạo đội thành công!');
          getTeamSales({});
          handleClear();
        }
      },
      onError: (error, variables) => {
        const { params } = variables;
        const teamSaleId = params[0].teamSaleId;

        teamSaleId && deleteSaleTeam(teamSaleId);
        toastError(`Tạo đội không thành công!. ${error.message}`);
        handleClear();
      },
    }),
    { isPending: isCreateInfoTeamSalePending, mutate: createInfoTeamSale } =
      useMutation({
        mutationFn: (params: IParamsCreate) =>
          SaleTeamApi.createTeamSale(params),
        onSuccess: (res: any, variables) => {
          const { leaderId } = variables;

          if (res?.data) {
            createManyEmployee({
              params: (variables.employees as TteamSalesDetail[]).map(
                (item) => ({
                  userId: item.user.id,
                  leaderYn: leaderId === item.user.id ? true : false,
                  teamSaleId: res.data.createTeamSales.id,
                  sloc: item.user.sloc,
                }),
              ),
              record: res.data.createTeamSales,
            });
          } else if (res?.errors) {
            handleClear();
            toastError(`Tạo đội thất bại!. ${res.errors[0].message}`);
          }
        },
        onError: (error) => {
          handleClear();
          toastError(`Tạo đội thất bại!. ${error.message}`);
        },
      }),
    { isPending: isGenerateTeamCodePending, mutate: handleCreateSalesTeam } =
      useMutation({
        mutationFn: ({
          plantId,
          teamTypeId,
        }: IParamsCreate & { isTypedName: boolean }) =>
          SaleTeamApi.generateTeamCodeByTypeAndPlantId({
            plantId: plantId,
            type: teamTypeId === TEAM_TYPE.stay ? 'tc' : 'ch',
          }),
        onSuccess: (res: any, variables) => {
          if (res.data) {
            const teamCode = res.data.genarareId[0];
            const { typeId, isTypedName, ...others } = variables;

            if (!teamCode?.id || !teamCode?.subId) {
              toastError('Tạo đội thất bại. Không thể tạo mã đội');
              return;
            }

            createInfoTeamSale({
              ...others,
              id: teamCode?.id,
              subId: teamCode?.subId,
              name: isTypedName
                ? variables.name
                : `${teamCode?.subId} - ${variables.name}`,
            });
          }
          if (res.errors) {
            const [fError, ..._] = res?.errors ?? [];
            const message = 'Tạo đội thất bại';

            toastError(`${message}. ${fError?.message}` || message);
          }
        },
        onError: (error) => {
          console.log(error);
        },
      }),
    {
      isPending: isUpdateManyTeamSaleDetail,
      mutate: updateManyTeamSaleDetail,
    } = useMutation({
      mutationFn: (params: {
        items: IParamsUpdateManyEmployee[];
        nextMutate?: () => void;
      }) => SaleTeamDetailApi.updateManyTeamSaleDetail(params.items),
      onSuccess: (res, variables) => {
        if (res?.data) {
          const { nextMutate } = variables;

          if (typeof nextMutate !== 'undefined') {
            nextMutate();
            return;
          }

          toastSuccess('Cập nhật đội thành công!');
          getTeamSales({});
          handleClear();
        }
      },
      onError: (error) => {
        toastError(
          `Cập nhật thành viên trong đội không thành công!. ${error.message}`,
        );
        handleClear();
      },
    }),
    {
      isPending: isDeleteManyTeamSaleDetail,
      mutate: deleteManyTeamSaleDetail,
    } = useMutation({
      mutationFn: (params: { ids: string[]; nextMutate?: () => void }) =>
        SaleTeamDetailApi.deleteManyTeamSaleDetail(params.ids),
      onSuccess: (res, variables) => {
        if (res?.data) {
          const { nextMutate } = variables;

          if (typeof nextMutate !== 'undefined') {
            nextMutate();
            return;
          }

          toastSuccess('Cập nhật đội thành công!');
          getTeamSales({});
          handleClear();
        }
      },
      onError: (error) => {
        toastError(
          `Cập nhật thành viên trong đội không thành công!. ${error.message}`,
        );
        handleClear();
      },
    }),
    { isPending: isUpdateSaleTeamPending, mutate: updateSaleTeam } =
      useMutation({
        mutationFn: (params: IParamsUpdate) =>
          SaleTeamApi.updateSaleTeam(params),
        onSuccess: (res: any, variables) => {
          const { leaderId } = variables;

          if (res?.data) {
            const { employees, id, currentEmployees, isLeaderChange } =
              variables;

            handleUpdateTeamSalesDetail({
              isLeaderChange,
              currentEmployees,
              employees,
              leaderId,
              salesTeamId: id,
            });
          } else if (res?.errors) toastError(res?.errors[0]?.message);
        },
        onError: (error) => {
          handleClear();
          toastError(`Cập nhật đội không thành công!. ${error.message}`);
        },
      });

  const handleUpdateTeamSalesDetail = async (params: {
    isLeaderChange: boolean;
    leaderId: string | undefined;
    salesTeamId: string;
    employees: TteamSalesDetail[];
    currentEmployees: TteamSalesDetail[];
  }) => {
    const {
      isLeaderChange,
      currentEmployees,
      employees,
      salesTeamId,
      leaderId,
    } = params;

    if (typeof employees === 'string' || typeof currentEmployees === 'string') {
      afterSuccessMutation();
      return;
    }

    const newEmployees = employees.filter(
      (e: any) =>
        !currentEmployees.some((ce: any) => e?.user?.id === ce.user.id),
    );
    const employeesDisable = currentEmployees.filter(
      (ce: any) => !employees.some((e: any) => e?.user?.id === ce.user.id),
    );
    const deleteEmployeeIds = employeesDisable
      .filter((item) => item.id)
      .map((employee) => employee.id);

    if (newEmployees.length > 0 && employeesDisable.length > 0) {
      deleteManyTeamSaleDetail({
        ids: deleteEmployeeIds,
        nextMutate: () =>
          createManyEmployee({
            params: newEmployees.map((employee) => {
              const employeeId = employee.user.id;

              return {
                userId: employeeId,
                leaderYn: leaderId === employeeId ? true : false,
                teamSaleId: salesTeamId,
                sloc: employee.sloc,
              };
            }),
            successMessage: 'Cập nhật đội thành công!',
            nextMutate: isLeaderChange
              ? () => {
                  const updateParams = {
                    items: [...newEmployees, ...currentEmployees]
                      .map((employee) => {
                        return employee.id &&
                          !deleteEmployeeIds.includes(employee.id)
                          ? {
                              id: employee.id,
                              teamSaleId: salesTeamId,
                              userId: employee.user.id,
                              leaderYn:
                                employee.user.id === leaderId ? true : false,
                            }
                          : undefined;
                      })
                      .filter(
                        (item) => item !== undefined,
                      ) as IParamsUpdateManyEmployee[],
                  };

                  updateParams.items.length > 0
                    ? updateManyTeamSaleDetail({
                        items: updateParams.items,
                      })
                    : afterSuccessMutation();
                }
              : undefined,
          }),
      });
    } else if (newEmployees.length > 0 && employeesDisable.length <= 0) {
      await createManyEmployee({
        params: newEmployees.map((employee) => {
          const employeeId = employee.user.id;

          return {
            userId: employeeId,
            leaderYn: leaderId === employeeId ? true : false,
            teamSaleId: salesTeamId,
            sloc: employee.sloc,
          };
        }),
        successMessage: 'Cập nhật đội thành công!',
        nextMutate: isLeaderChange
          ? () => {
              updateManyTeamSaleDetail({
                items: currentEmployees.map((employee) => ({
                  id: employee.id,
                  teamSaleId: salesTeamId,
                  userId: employee.user.id,
                  leaderYn: employee.user.id === leaderId ? true : false,
                })),
              });
            }
          : undefined,
      });
    } else if (newEmployees.length <= 0 && employeesDisable.length > 0) {
      deleteManyTeamSaleDetail({
        ids: deleteEmployeeIds,
        nextMutate: isLeaderChange
          ? () => {
              const updateParams = {
                items: currentEmployees
                  .map((employee) =>
                    deleteEmployeeIds.includes(employee.id)
                      ? undefined
                      : {
                          id: employee.id,
                          teamSaleId: salesTeamId,
                          userId: employee.user.id,
                          leaderYn:
                            employee.user.id === leaderId ? true : false,
                        },
                  )
                  .filter(
                    (item) => item !== undefined,
                  ) as IParamsUpdateManyEmployee[],
              };

              updateParams.items.length > 0
                ? updateManyTeamSaleDetail({
                    items: updateParams.items,
                  })
                : afterSuccessMutation();
            }
          : undefined,
      });
    } else if (isLeaderChange) {
      updateManyTeamSaleDetail({
        items: currentEmployees.map((employee) => ({
          id: employee.id,
          teamSaleId: salesTeamId,
          userId: employee.user.id,
          leaderYn: employee.user.id === leaderId ? true : false,
        })),
      });
    } else afterSuccessMutation();
  };

  const afterSuccessMutation = () => {
    handleClear();
    getTeamSales({});
    toastSuccess('Cập nhật đội thành công!');
  };

  const getTeamSales = ({ afterMutation }: { afterMutation?: () => void }) => {
    if (typeof user === 'undefined') return;

    if (user.isAdmin || user.isHO) getTeamSaleCbb({ pageSize: 10 });
    if (user.isCN)
      getTeamSaleCbb({
        pageSize: 10,
        plantId: user.plantCode,
        ...(user.isQLD && { managerId: user.id }),
      });
    if (user.isSale)
      getTeamSaleCbb({
        pageSize: 10,
        plantId: user.plantCode,
      });

    getTeamSalesMutation.mutate({
      plantId: user.isHO || user.isAdmin ? '5000' : user.plantCode,
      ...(user.isQLD && { managerId: user.id }),
      ...paramsFilter,
      page,
      pageSize,
      afterMutation: afterMutation,
    });
  };

  useEffect(() => {
    if (firR.current) {
      firR.current = false;
      return;
    }

    getTeamSalesMutation.isPending ||
      getTeamSalesMutation.mutate({
        ...defaultFilter,
        ...paramsFilter,
        page,
        pageSize,
      });
  }, [page, pageSize, paramsFilter]);

  useEffect(() => {
    setIsOpenFilter(false);

    // if (edit === 0) form.resetFields();
  }, [edit]);

  useEffect(() => {
    if (!isAddRow) return;
    setEdit(0);
  }, [data]);

  useEffect(() => {
    setTitle('Đội bán hàng');
    setBreadcrumb([{ title: 'Kế hoạch' }, { title: 'Đội bán hàng' }]);
  }, [user]);

  const handleAndRow = async () => {
    if (childRef && childRef.current) {
      childRef.current.handleAddRow();
    }
  };

  const handleExportExcel = async () => {
    if (childRef && childRef.current) {
      getTeamSalesMutation.mutate({
        ...defaultFilter,
        ...paramsFilter,
        page: 1,
        pageSize:
          getTeamSalesMutation.data?.data?.getTeamSalesList?.pageInfo
            .totalItems,
        export: { headerRow: childRef.current.getHeaderRow() },
      });
    }
  };

  const handleImportData = useCallback(() => {
    setIsOpenImportModal(true);
  }, []);

  const reFetchTeamSale = useCallback(() => {
    if (filterRef && filterRef.current) filterRef.current.handleCancel();
  }, [filterRef]);

  const handleOnCancelFilter = useCallback(() => {
    setParamsFilter({} as IFilterTeamSale);
    setIsOpenFilter(false);
  }, []);

  const handleOnOkFilter = (values: any) => {
    navigate(`?page=${values?.page ?? 1}&page_size=${pageSize}`);
    setParamsFilter(values);
  };

  const handleClear = () => {
    if (childRef && childRef.current) childRef.current.handleClear();
  };

  const replaceData = () => {
    if (childRef && childRef.current) childRef.current.replaceData();
  };

  const handleSave = (record: any, form: any, index: number) => {
    setIsAddRow(false);

    const leaderId = form['leader']?.split('-')[0].trim();
    const slocs = (record.slocsData as DefaultOptionType[])
      .map((sloc) => sloc.value?.toString().trim())
      .join('; ');

    if (!data?.items[index].id) {
      handleCreateSalesTeam({
        ...form,
        teamTypeId:
          TEAM_TYPE_OPTIONS.find((item) => item.label === form?.teamTypeName)
            ?.value ?? TEAM_TYPE_OPTIONS[0].value,
        isWork: true,
        sloc: slocs,
        leaderId: leaderId,
        employees: record.employees,
        isTypedName: record?.isTypedName,
      });

      replaceData();
    } else {
      updateSaleTeam({
        ...form,
        id: record?.id,
        teamTypeId:
          TEAM_TYPE_OPTIONS.find((item) => item.label === form?.teamTypeName)
            ?.value ?? TEAM_TYPE_OPTIONS[0].value,
        employees: record?.employees ?? [],
        currentEmployees: record?.currentEmployees ?? [],
        leaderId: leaderId,
        useYn: record.statusName === STATUS_NAME_ST.ACTIVE,
        sloc: slocs,
        manageId: form.manageId || null,
        isLeaderChange:
          leaderId !== (record?.leader ?? '').split('-')[0].trim(),
      });
    }

    setEdit(-1);
  };

  const handleSwitchStatusSalesTeam = (record: any) => {
    const isActivating = record.statusName === STATUS_NAME_ST.ACTIVE;
    const { modifyDate, modifyId, ...others } = record;

    updateSaleTeam({
      ...others,
      useYn: !isActivating,
      isStatusChange: true,
    });
  };

  return (
    <>
      <Row gutter={[8, 16]} align={'middle'} justify={'end'}>
        <Col>
          <ButtonFilter
            isOpen={isOpenFilter}
            setIsOpen={setIsOpenFilter}
            disabled={edit !== -1}
          />
        </Col>
        <Col>
          <ButtonExport onClick={handleExportExcel} disabled={edit !== -1} />
        </Col>
        {user?.isHO || user?.isAdmin || user?.isSale ? (
          <></>
        ) : (
          <Col>
            <ButtonImport onClick={handleImportData} disabled={edit !== -1} />
          </Col>
        )}
        {user?.isHO || user?.isAdmin || user?.isSale ? (
          <></>
        ) : (
          <Col>
            <Button
              fill
              iconBefore={<PlusOutlined />}
              title="Tạo đội"
              handleOnclick={handleAndRow}
              disabled={edit !== -1}
            />
          </Col>
        )}
      </Row>
      <UploadModal
        templateFileName="Đội bán hàng - Template"
        importId={IMPORT_SALES_TEAM}
        open={isOpenImportModal}
        title={'Thêm mới đội bán hàng'}
        setOpen={setIsOpenImportModal}
        refetch={reFetchTeamSale}
      />
      <FilterSaleTeamPage
        saleTeamMutate={{
          data: teamData.items,
          totalItems: teamData.totalItems,
          isPending: isGetTeamSaleCbb,
          mutate: getTeamSaleCbb,
        }}
        isOpen={isOpenFilter}
        defaultFilter={defaultFilter}
        ref={filterRef}
        onCancel={handleOnCancelFilter}
        onFilter={handleOnOkFilter}
      />
      <TableSaleTeamPage
        data={{ value: data, setValue: setData }}
        edit={{ value: edit, setValue: setEdit }}
        ref={childRef}
        isAddRow={isAddRow}
        loading={
          getTeamSalesMutation.isPending ||
          isGenerateTeamCodePending ||
          isUpdateSaleTeamPending ||
          isCreateEmployeeMutationPending ||
          isDeleteSaleTeamPending ||
          isUpdateManyTeamSaleDetail ||
          isCreateInfoTeamSalePending ||
          isDeleteManyTeamSaleDetail
        }
        onSaveRow={handleSave}
        onSwitchRow={handleSwitchStatusSalesTeam}
        getTeamSales={getTeamSales}
        setIsAddRow={setIsAddRow}
      />
    </>
  );
};

export default SaleTeamPage;
